import {
  BasicPermission,
  createPermission,
  ResourcePermission,
} from '@backstage/plugin-permission-common';
import { RESOURCE_TYPE_CATALOG_ENTITY } from '@backstage/plugin-catalog-common/alpha';

/**
 * Limited read access to a catalog entity. The custom permission policy
 * decides conditionally on the entity (see `HasLimitedAccess` and the
 * `HasLimitedClaimsAccess` rules in permission-backend-module-custom-policy).
 */
export const limitedAccessPermission: ResourcePermission<'catalog-entity'> =
  createPermission({
    name: 'limited-access-permission',
    attributes: { action: 'read' },
    resourceType: RESOURCE_TYPE_CATALOG_ENTITY,
  });

/**
 * Read access to the group auth key of a catalog entity.
 */
export const groupAuthPermission: ResourcePermission<'catalog-entity'> =
  createPermission({
    name: 'group-auth-key-display',
    attributes: { action: 'read' },
    resourceType: RESOURCE_TYPE_CATALOG_ENTITY,
  });

/**
 * Whether the user is a member of at least one group.
 */
export const groupExistForUserPermission: BasicPermission = createPermission({
  name: 'group-exist-for-user',
  attributes: { action: 'read' },
});

/**
 * Change or delete another user's `user_info` record: their role, their
 * research panel interest, or the record itself. Users can always change and
 * delete their own record without it; the custom permission policy grants it
 * to admins only.
 */
export const userInfoAdministerPermission: BasicPermission = createPermission({
  name: 'user-info.administer',
  attributes: {},
});

/**
 * Access to MCP Chat: chat, tools, status and conversations. mcp-chat-backend
 * registers and enforces it; the custom permission policy grants it to members
 * of `group:default/aiusers` and to admins.
 */
export const mcpChatAccessPermission: BasicPermission = createPermission({
  name: 'mcp-chat.access',
  attributes: {},
});

/**
 * Read access to the DevTools admin pages (catalog health and the later
 * subpages) and to their backend routes. The custom permission policy grants
 * it to members of `group:default/admins` only.
 */
export const devtoolsAdminReadPermission: BasicPermission = createPermission({
  name: 'devtools.admin.read',
  attributes: { action: 'read' },
});

/**
 * The actions on the DevTools admin pages, such as refreshing an entity or a
 * location and cancelling a scaffolder task. The custom permission policy
 * grants it to members of `group:default/admins` only.
 */
export const devtoolsAdminActionPermission: BasicPermission = createPermission({
  name: 'devtools.admin.action',
  attributes: {},
});
